import { useEffect, useMemo, useState } from "react";
import { ReactFlow, Background, Controls, MiniMap, MarkerType } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { TrackNode, ClosedNode, EndNode, NoteNode } from "@/components/crm/SopCanvasNodes";
import { Track, Outcomes, EffectSelect, newTrack, dropTrack } from "@/components/crm/SopEditor";
import { effectOf } from "@/components/crm/SopFlow";
import { Plus, LayoutGrid, X, StickyNote, Trash2 } from "lucide-react";

const TYPES = { track: TrackNode, closed: ClosedNode, end: EndNode, note: NoteNode };
const ENDS = [["x:close:Lost", "Lost", "close"], ["x:close:Shelved", "Shelved", "close"], ["x:rebook", "Rebook → new meeting", "rebook"], ["x:client_date", "Wait for client's date", "client_date"]];
const LINES = [["smoothstep", "Elbow"], ["default", "Curved"], ["straight", "Straight"]];
const NOTE_COLORS = ["#facc15", "#60a5fa", "#4ade80", "#f472b6", "#a3a3a3"];
const keep = (e) => Object.fromEntries(Object.entries(e || {}).filter(([k]) => ["set", "meet_held", "proposed", "qualification"].includes(k)));
const srcKey = (id) => (id === "closed" ? "closed" : id.slice(2));
const uid = () => Math.random().toString(36).slice(2, 8);

function defaults(sop) {
  const ks = ["meeting", ...Object.keys(sop.tracks).filter((k) => k !== "meeting")];
  const p = {};
  ks.forEach((k, i) => { p[`t:${k}`] = { x: (i % 4) * 380, y: Math.floor(i / 4) * 460 }; });
  p.closed = { x: 4 * 380, y: 0 };
  ENDS.forEach(([id], i) => { p[id] = { x: 5 * 380, y: i * 110 }; });
  return p;
}

function targetOf(e) {
  if (e.type === "track") return `t:${e.track}`;
  if (e.type === "close") return `x:close:${e.stage}`;
  if (e.type === "rebook") return "x:rebook";
  if (e.type === "client_date") return "x:client_date";
  return null;
}

function effectFor(target, old) {
  if (target.startsWith("t:")) return { type: "track", track: target.slice(2) };
  if (target.startsWith("x:close:")) return { type: "close", stage: target.slice(8), reason: old.type === "close" ? old.reason : "pick" };
  if (target === "x:rebook") return { type: "rebook" };
  if (target === "x:client_date") return { type: "client_date" };
  return null;
}

function build(sop, pos, sel, dims) {
  const d = defaults(sop);
  const cv = sop.canvas || {};
  const at = (id) => pos[id] || d[id] || { x: 0, y: -200 };
  const withDims = (n) => (dims[n.id] ? { ...n, measured: dims[n.id], width: dims[n.id].width, height: dims[n.id].height } : n);
  const nodes = [
    ...Object.keys(sop.tracks).map((k) => ({ id: `t:${k}`, type: "track", position: at(`t:${k}`), data: { sop, k, entry: k === "meeting", selected: sel?.node === `t:${k}` } })),
    { id: "closed", type: "closed", position: at("closed"), data: { sop, selected: sel?.node === "closed" } },
    ...ENDS.map(([id, label, kind]) => ({ id, type: "end", position: at(id), data: { id: id.replace(/[:]/g, "-"), label, kind } })),
    ...(cv.notes || []).map((n) => ({ id: `n:${n.id}`, type: "note", position: at(`n:${n.id}`), data: { ...n, selected: sel?.node === `n:${n.id}` } })),
  ].map(withDims);
  const type = cv.line || "smoothstep";
  const edges = [];
  [...Object.keys(sop.tracks), "closed"].forEach((k) => (sop.outcomes[k] || []).forEach((o) => {
    const e = effectOf(sop, k, o);
    const target = targetOf(e);
    if (!target || (e.type === "track" && !sop.tracks[e.track])) return;
    const on = sel?.edge?.k === k && sel.edge.o === o;
    const color = on ? "var(--ai)" : e.type === "close" ? "var(--breach)" : "var(--text-faint)";
    edges.push({ id: `${k}|${o}`, source: k === "closed" ? "closed" : `t:${k}`, sourceHandle: o, target, type, animated: e.type === "track",
      label: e.type === "close" && e.reason ? (e.reason === "pick" ? "pick reason" : e.reason) : undefined,
      style: { stroke: color, strokeWidth: on ? 2.5 : 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, color },
      labelStyle: { fill: "var(--text-dim)", fontSize: 10 }, labelBgStyle: { fill: "var(--panel)" }, data: { k, o } });
  }));
  (cv.links || []).forEach((l) => {
    const on = sel?.link === l.id;
    const color = on ? "var(--ai)" : l.color || "#facc15";
    edges.push({ id: `l:${l.id}`, source: l.source, sourceHandle: l.sourceHandle || "free", target: l.target, targetHandle: l.targetHandle, type,
      label: l.label || undefined, style: { stroke: color, strokeWidth: on ? 2.5 : 1.5, strokeDasharray: "6 4" },
      markerEnd: { type: MarkerType.ArrowClosed, color }, labelStyle: { fill: "var(--text)", fontSize: 11 }, labelBgStyle: { fill: "var(--panel)" }, data: { link: l.id } });
  });
  return { nodes, edges };
}

function LinkPanel({ sop, id, setCanvas, close }) {
  const cv = sop.canvas || {};
  const l = (cv.links || []).find((x) => x.id === id);
  if (!l) return null;
  const upd = (p) => setCanvas({ links: cv.links.map((x) => (x.id === id ? { ...x, ...p } : x)) });
  return (
    <div className="space-y-3 text-xs" data-testid="canvas-link-panel">
      <div className="section-label">Arrow (note only, doesn't change the SOP)</div>
      <input className="w-full px-2 py-1.5 rounded border text-sm" placeholder="Label" value={l.label || ""} onChange={(e) => upd({ label: e.target.value })} data-testid="canvas-link-label" />
      <div className="flex gap-1.5">{NOTE_COLORS.map((c) => <button key={c} onClick={() => upd({ color: c })} className={`w-6 h-6 rounded-full border-2 ${l.color === c ? "border-[var(--text)]" : "border-transparent"}`} style={{ background: c }} data-testid={`canvas-link-color-${c.slice(1)}`} />)}</div>
      <button className="btn text-xs hover:text-[var(--breach)]" onClick={() => { setCanvas({ links: cv.links.filter((x) => x.id !== id) }); close(); }} data-testid="canvas-link-delete"><Trash2 size={12} />Delete arrow</button>
    </div>
  );
}

function NotePanel({ sop, id, setCanvas, close }) {
  const cv = sop.canvas || {};
  const n = (cv.notes || []).find((x) => x.id === id);
  if (!n) return null;
  const upd = (p) => setCanvas({ notes: cv.notes.map((x) => (x.id === id ? { ...x, ...p } : x)) });
  return (
    <div className="space-y-3 text-xs" data-testid="canvas-note-panel">
      <div className="section-label">Note</div>
      <textarea rows={6} autoFocus className="w-full px-2 py-1.5 rounded border text-sm" value={n.text} onChange={(e) => upd({ text: e.target.value })} data-testid="canvas-note-text" />
      <div className="flex gap-1.5">{NOTE_COLORS.map((c) => <button key={c} onClick={() => upd({ color: c })} className={`w-6 h-6 rounded-full border-2 ${n.color === c ? "border-[var(--text)]" : "border-transparent"}`} style={{ background: c }} data-testid={`canvas-note-color-${c.slice(1)}`} />)}</div>
      <button className="btn text-xs hover:text-[var(--breach)]" onClick={() => { setCanvas({ notes: cv.notes.filter((x) => x.id !== id), links: (cv.links || []).filter((l) => l.source !== `n:${id}` && l.target !== `n:${id}`) }); close(); }} data-testid="canvas-note-delete"><Trash2 size={12} />Delete note</button>
    </div>
  );
}

function Side({ sop, sel, set, setCanvas, close }) {
  const setTrack = (k, p) => set({ tracks: { ...sop.tracks, [k]: { ...sop.tracks[k], ...p } } });
  let body = null;
  if (sel.link) body = <LinkPanel sop={sop} id={sel.link} setCanvas={setCanvas} close={close} />;
  else if (sel.node?.startsWith("n:")) body = <NotePanel sop={sop} id={sel.node.slice(2)} setCanvas={setCanvas} close={close} />;
  else if (sel.edge) {
    const { k, o } = sel.edge;
    body = (
      <div className="space-y-3 text-xs" data-testid="canvas-edge-panel">
        <div><div className="section-label">Outcome</div><div className="text-sm font-medium">{o}</div></div>
        <div><div className="section-label mb-1">Leads to</div>
          <EffectSelect sop={sop} k={k} o={o} onChange={(e) => set({ effects: { ...sop.effects, [`${k}:${o}`]: e } })} testid="canvas-edge-goto" /></div>
        <div className="text-[11px] text-[var(--text-faint)]">Tip: drag the arrow's end onto another card to re-route it.</div>
        <button className="btn text-xs hover:text-[var(--breach)]" onClick={() => set({ effects: { ...sop.effects, [`${k}:${o}`]: { ...keep(effectOf(sop, k, o)), type: "none" } } })} data-testid="canvas-edge-disconnect">Disconnect (log only)</button>
      </div>
    );
  } else if (sel.node === "closed") {
    body = <div data-testid="canvas-closed-panel"><div className="text-sm font-medium">Lost / Shelved → revive</div><Outcomes sop={sop} k="closed" set={set} /></div>;
  } else if (sel.node?.startsWith("t:")) {
    const k = sel.node.slice(2);
    if (!sop.tracks[k]) return null;
    body = <Track k={k} t={sop.tracks[k]} sop={sop} set={set} setTrack={(p) => setTrack(k, p)} onRemove={["meeting", "won"].includes(k) ? null : () => { set(dropTrack(sop, k)); close(); }} />;
  }
  if (!body) return null;
  return (
    <aside className="absolute top-14 right-3 bottom-3 w-[min(560px,92%)] overflow-auto rounded-xl border border-[var(--border-strong)] bg-[var(--panel)] p-3 shadow-2xl z-10" data-testid="canvas-side">
      <button className="btn-ghost p-1 float-right" onClick={close} data-testid="canvas-side-close"><X size={14} /></button>
      {body}
    </aside>
  );
}

function Toolbar({ sop, set, setCanvas, setPos, editable }) {
  const cv = sop.canvas || {};
  return (
    <div className="absolute top-3 left-3 right-3 flex flex-wrap items-center gap-1.5 z-10 pointer-events-none [&>*]:pointer-events-auto">
      {editable && <>
        <button className="btn text-xs bg-[var(--panel)]" onClick={() => set(newTrack(sop))} data-testid="canvas-add-track"><Plus size={12} />Track</button>
        <button className="btn text-xs bg-[var(--panel)]" onClick={() => setCanvas({ notes: [...(cv.notes || []), { id: uid(), text: "Note", color: "#facc15" }] })} data-testid="canvas-add-note"><StickyNote size={12} />Note</button>
        <button className="btn text-xs bg-[var(--panel)]" onClick={() => { setPos({}); set({ layout: {} }); }} data-testid="canvas-auto-arrange"><LayoutGrid size={12} />Auto-arrange</button>
      </>}
      <div className="flex rounded-md border border-[var(--border-strong)] overflow-hidden text-xs bg-[var(--panel)]" data-testid="canvas-line-style">
        {LINES.map(([v, l]) => (
          <button key={v} disabled={!editable} onClick={() => setCanvas({ line: v })} data-testid={`canvas-line-${v}`}
            className={`px-2.5 py-1 ${(cv.line || "smoothstep") === v ? "bg-[var(--panel-2)] text-[var(--text)]" : "text-[var(--text-dim)]"}`}>{l}</button>
        ))}
      </div>
      <span className="text-[11px] text-[var(--text-faint)] px-2">{editable ? "Drag anything · drag a dot to a card to connect · drag an arrow's end to re-route · click to edit · Backspace deletes" : "View only"}</span>
    </div>
  );
}

export default function SopCanvas({ sop, onChange, editable }) {
  const [pos, setPos] = useState(sop.layout || {});
  const [sel, setSel] = useState(null);
  const [dims, setDims] = useState({});
  useEffect(() => { setSel(null); }, [sop._id]);
  useEffect(() => { setPos(sop.layout || {}); }, [sop._id, sop.layout]);
  const { nodes, edges } = useMemo(() => build(sop, pos, sel, dims), [sop, pos, sel, dims]);
  const set = (p) => onChange({ ...sop, ...p });
  const setCanvas = (p) => set({ canvas: { ...(sop.canvas || {}), ...p } });
  const cv = sop.canvas || {};
  const onNodesChange = (changes) => changes.forEach((c) => {
    if (c.type === "position" && c.position) setPos((p) => ({ ...p, [c.id]: c.position }));
    if (c.type === "dimensions" && c.dimensions) setDims((d) => ({ ...d, [c.id]: c.dimensions }));
  });
  const route = (k, o, target) => {
    const old = effectOf(sop, k, o);
    const e = effectFor(target, old);
    if (!e) return false;
    set({ effects: { ...sop.effects, [`${k}:${o}`]: { ...keep(old), ...e } } });
    setSel({ edge: { k, o } });
    return true;
  };
  const addLink = (c) => {
    const id = uid();
    setCanvas({ links: [...(cv.links || []), { id, source: c.source, sourceHandle: c.sourceHandle, target: c.target, targetHandle: c.targetHandle, label: "", color: "#facc15" }] });
    setSel({ link: id });
  };
  const onConnect = (c) => {
    if (c.source === c.target) return;
    const isOutcome = !c.source.startsWith("n:") && c.sourceHandle && !c.sourceHandle.startsWith("free");
    if (isOutcome && route(srcKey(c.source), c.sourceHandle, c.target)) return;
    addLink(c);
  };
  const onConnectEnd = (ev, st) => {
    if (!editable || st?.isValid || !st?.fromNode) return;
    const pt = ev.changedTouches?.[0] || ev;
    const el = document.elementFromPoint(pt.clientX, pt.clientY)?.closest(".react-flow__node");
    const target = el?.getAttribute("data-id");
    if (!target || target === st.fromNode.id) return;
    onConnect({ source: st.fromNode.id, sourceHandle: st.fromHandle?.id, target, targetHandle: null });
  };
  const onReconnect = (old, c) => {
    if (old.data?.link) return setCanvas({ links: cv.links.map((l) => (l.id === old.data.link ? { ...l, source: c.source, sourceHandle: c.sourceHandle, target: c.target, targetHandle: c.targetHandle } : l)) });
    if (old.data?.k) route(old.data.k, old.data.o, c.target);
  };
  const remove = () => {
    if (sel?.edge) set({ effects: { ...sop.effects, [`${sel.edge.k}:${sel.edge.o}`]: { ...keep(effectOf(sop, sel.edge.k, sel.edge.o)), type: "none" } } });
    else if (sel?.link) setCanvas({ links: cv.links.filter((l) => l.id !== sel.link) });
    else if (sel?.node?.startsWith("n:")) setCanvas({ notes: cv.notes.filter((n) => `n:${n.id}` !== sel.node), links: (cv.links || []).filter((l) => l.source !== sel.node && l.target !== sel.node) });
    else return false;
    setSel(null);
    return true;
  };
  const onKey = (ev) => {
    if (!editable || !["Backspace", "Delete"].includes(ev.key) || /input|textarea|select/i.test(ev.target.tagName)) return;
    if (remove()) ev.preventDefault();
  };
  return (
    <div tabIndex={0} onKeyDown={onKey} className="relative h-[75vh] min-h-[520px] rounded-xl border border-[var(--border)] overflow-hidden bg-[var(--bg)] outline-none" data-testid="sop-canvas">
      <ReactFlow nodes={nodes} edges={edges} nodeTypes={TYPES} colorMode={document.documentElement.classList.contains("light") ? "light" : "dark"} fitView minZoom={0.15} maxZoom={2}
        nodesDraggable={editable} nodesConnectable={editable} elementsSelectable={editable} edgesFocusable={editable} edgesReconnectable={editable}
        connectionMode="loose" connectionRadius={30} snapToGrid snapGrid={[10, 10]}
        onNodesChange={onNodesChange} onNodeDragStop={(_, n, ns) => editable && set({ layout: { ...pos, ...Object.fromEntries((ns || [n]).map((x) => [x.id, x.position])) } })}
        onConnect={onConnect} onConnectEnd={onConnectEnd} onReconnect={onReconnect} onNodeClick={(_, n) => editable && n.type !== "end" && setSel({ node: n.id })}
        onEdgeClick={(_, e) => editable && setSel(e.data.link ? { link: e.data.link } : { edge: e.data })} onPaneClick={() => setSel(null)}
        deleteKeyCode={null} multiSelectionKeyCode="Shift" proOptions={{ hideAttribution: true }}>
        <Background gap={20} size={1} color="var(--border)" />
        <Controls showInteractive={false} />
        <MiniMap pannable zoomable maskColor="rgba(0,0,0,0.5)" style={{ background: "var(--panel)" }} nodeColor={(n) => (n.type === "note" ? n.data.color : "var(--panel-2)")} />
      </ReactFlow>
      <Toolbar sop={sop} set={set} setCanvas={setCanvas} setPos={setPos} editable={editable} />
      {editable && sel && <Side sop={sop} sel={sel} set={set} setCanvas={setCanvas} close={() => setSel(null)} />}
    </div>
  );
}
